import {
  DocumentResult,
  GetAllCompanyDocumentsResponse,
  GetAllCompanyDocumentsParams
} from '@/types/companyDocumentTypes';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const DOCUMENTS_ENDPOINT = `${API_BASE_URL}/companydocuments`;

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed.'
        );
      }
      return token;
    }
    throw error;
  }
};

export const getAllCompanyDocuments = async (
  params: GetAllCompanyDocumentsParams,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<GetAllCompanyDocumentsResponse> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const queryParams = new URLSearchParams();
  if (params.pageNumber !== undefined) {
    queryParams.append('pageNumber', params.pageNumber.toString());
  }
  if (params.pageSize !== undefined) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.searchQuery !== undefined && params.searchQuery.trim() !== '') {
    queryParams.append('searchQuery', params.searchQuery);
  }
  if (params.companyId !== undefined) {
    queryParams.append('companyId', params.companyId);
  }
  if (params.collectionId !== undefined) {
    queryParams.append('collectionId', params.collectionId);
  }
  if (params.sortBy !== undefined && params.sortBy.trim() !== '') {
    queryParams.append('sortBy', params.sortBy);
  }
  if (params.sortAscending !== undefined) {
    queryParams.append('sortAscending', params.sortAscending.toString());
  }
  queryParams.append('customerScopeOnly', 'false');

  const queryString = queryParams.toString();
  const url = queryString
    ? `${DOCUMENTS_ENDPOINT}?${queryString}`
    : DOCUMENTS_ENDPOINT;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      errorData.message ||
        `Failed to fetch documents. Status: ${response.status}`
    );
  }
  return (await response.json()) as GetAllCompanyDocumentsResponse;
};
